import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import buyerModule from '../api/modules/buyer.module';
import { useAuth } from '../hooks/useAuth';
import { invalidateFavourites } from './favouriteQueries';
import { readGuestFavourites, removeGuestFavourites, toggleGuestFavourite } from './guestFavourites';
import { showToast } from '../utils/toast';

/**
 * Favourites, for every surface that has any.
 *
 * SIX SURFACES USED TO ANSWER THIS QUESTION THEMSELVES and disagreed: the
 * browse grids kept a local Set that persisted nothing for anyone, the two
 * detail pages called the API and bounced a signed-out buyer to /login, and the
 * collection page could not render for a guest at all. One hook, one answer.
 *
 * The mode split lives here and nowhere else. A caller asks whether a card is
 * saved and toggles it; whether that means localStorage or a round trip is not
 * a question a heart should be asking.
 *
 * `product` on a favourites row is the CARD ID -- a legacy alias, not a
 * CollectrProduct id (`get_favourites` emits `"product": str(fav.card_id)`).
 * Verified against QA, where every row has `card_id` set and `product_id` null.
 */
const SAVED_CARDS_KEY = ['saved-cards', 'all'];

/* Big enough to hold any account's list in one read: this hook only needs the
   IDS, and paging them would make "is this card saved" depend on which page you
   happened to fetch. Only actually honoured since `getSavedCards` started
   sending `page_size` -- it used to send `offset`, which `get_favourites` does
   not read, so this asked for 200 and got the view's default of 10 and every
   favourite past the tenth reported `isSaved === false`. */
const MEMBERSHIP_PAGE_SIZE = 200;

export function useFavourites() {
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const isGuest = !isAuthenticated;

  const serverQuery = useQuery({
    queryKey: SAVED_CARDS_KEY,
    enabled: isAuthenticated,
    queryFn: async () => {
      const result = await buyerModule.getSavedCards({ page: 1, limit: MEMBERSHIP_PAGE_SIZE });
      if (!result.success) throw new Error(result.error || 'Failed to load favourites');
      return result.data?.favourites || [];
    },
  });

  /* Guest ids are read on every render rather than mirrored into state: the
     store is the single source of truth and another tab may have written it. */
  const guestIds = isGuest ? readGuestFavourites() : [];

  const ids = useMemo(() => {
    if (isGuest) return guestIds;
    return (serverQuery.data || []).map((row) => String(row.product)).filter(Boolean);
  }, [isGuest, guestIds.join(','), serverQuery.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const idSet = useMemo(() => new Set(ids), [ids]);

  const serverToggle = useMutation({
    mutationFn: async ({ cardId, saved }) => {
      const result = saved
        ? await buyerModule.unsaveCard(cardId)
        : await buyerModule.saveCard(cardId);
      if (!result?.success) throw new Error(result?.error || 'Failed to update favourites');
      return result;
    },
    /* The WHOLE family, not just this hook's own key. `invalidateQueries`
       matches on a prefix, so `SAVED_CARDS_KEY` (`['saved-cards','all']`)
       refreshes membership and nothing else -- not the `/collection` grid on
       `['saved-cards',{page,limit}]`, not the dashboard's summary tile, not
       the profile preview. With the app's five-minute global `staleTime`
       (App.js) that meant hearting a card on /marketplace and opening
       /collection a minute later showed a collection without it in. */
    onSuccess: () => invalidateFavourites(queryClient),
    /* SAID HERE, NOT AT THE CALL SITE, because two of the four call sites
       forgot. The card page and the listing page both wrap `toggle` in a
       try/catch and toast; `Marketplace.jsx` and `SellerStore.jsx` pass it
       straight through as `onToggleSaved={(cardId) => favourites.toggle(cardId)}`.
       `mutateAsync` rejects on failure, so from the browse grid or a seller
       store a failed save was a dropped promise -- no toast, no error, the
       heart simply never filling. Putting it on the mutation means every
       present and future caller reports the failure whether it remembers to
       or not. */
    onError: (error) =>
      showToast.error(error?.message || 'Could not update your collection'),
  });

  const toggle = useCallback(
    async (cardId) => {
      if (!cardId) return;
      if (isGuest) {
        toggleGuestFavourite(cardId);
        /* Nothing here re-renders on a localStorage write, so nudge the tree the
           same way the server branch does. */
        queryClient.setQueryData(['guest-favourites'], Date.now());
        return;
      }
      await serverToggle.mutateAsync({ cardId, saved: idSet.has(cardId) });
    },
    [isGuest, idSet, serverToggle, queryClient],
  );

  /* Drops ids outright rather than toggling them. `/collection` is the caller:
     when `cards-by-ids/` cannot resolve a saved id the card is gone from the
     catalog for good, and leaving it in localStorage means a grid that
     disagrees with its own count and a dead id the sign-in merge has to chew
     on. Guest-only -- a signed-in buyer's list is the server's. */
  const dropIds = useCallback(
    (idsToDrop) => {
      if (!isGuest || !idsToDrop?.length) return;
      removeGuestFavourites(idsToDrop);
      queryClient.setQueryData(['guest-favourites'], Date.now());
    },
    [isGuest, queryClient],
  );

  /* Subscribing to the nudge above is what turns a localStorage write into a
     re-render for every consumer of this hook. */
  useQuery({ queryKey: ['guest-favourites'], enabled: isGuest, queryFn: () => 0, staleTime: Infinity });

  return {
    ids,
    dropIds,
    isSaved: useCallback((cardId) => idSet.has(cardId), [idSet]),
    toggle,
    count: ids.length,
    isGuest,
    isLoading: isAuthenticated && serverQuery.isPending,
  };
}

export default useFavourites;
